import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, User, MessageCircle, LogOut, Sparkles, Bell, ChevronDown, Check } from 'lucide-react';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { getPhotoUrl } from '../utils/photo';
import { formatDistanceToNow } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import IOSInstallPrompt from '../components/IOSInstallPrompt';

/*  tweddi — Discover (/discover). tweddi identity: cream ground, Inclusive Sans,
    near-mono ink, apricot only as rare accents, woven-rings monogram logo.
    Swipe / match / bouquet-toss / event-switcher / notifications logic 100% kept. */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INK = '#141313';
const CREAM = '#FFFDF8';
const CONFETTI_COLORS = ['#141313', '#F4A26B', '#D9E2D2', '#EBC4C0', '#FFFDF8'];

/* woven monogram — cream mark on the ink logo tile (bg = ink) */
function TweddiLogo() {
  return (
    <svg viewBox="-60 -60 664 520" width="30" height={30*(520/664)} style={{ overflow: 'visible', display: 'block' }} aria-label="tweddi">
      <circle cx="200" cy="200" r="108" fill="none" stroke={CREAM} strokeWidth="66" />
      <circle cx="344" cy="200" r="108" fill="none" stroke={INK}   strokeWidth="108" />
      <circle cx="344" cy="200" r="108" fill="none" stroke={CREAM} strokeWidth="66" />
      <path d="M 206.01 307.83 A 108 108 0 0 0 282.00 270.28" fill="none" stroke={INK}   strokeWidth="108" />
      <path d="M 196.23 307.93 A 108 108 0 0 0 285.11 266.49" fill="none" stroke={CREAM} strokeWidth="66" />
    </svg>
  );
}

function ConfettiEffect() {
  const pieces = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 2}s`,
    color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
    size: 5 + Math.random() * 6,
    rotation: Math.random() * 360,
  }));
  return (
    <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 999, overflow: 'hidden' }}>
      {pieces.map((p) => (
        <div key={p.id} style={{
          position: 'absolute', top: '-10px', left: p.left,
          width: p.size, height: p.size,
          backgroundColor: p.color,
          borderRadius: Math.random() > 0.5 ? '50%' : '2px',
          transform: `rotate(${p.rotation}deg)`,
          animation: `cf-fall 3s ${p.delay} ease-in forwards`
        }} />
      ))}
      <style>{`@keyframes cf-fall { to { transform: translateY(100vh) rotate(720deg); opacity: 0; } }`}</style>
    </div>
  );
}

export default function DiscoverPage() {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [swipeClass, setSwipeClass] = useState('');
  const [showMatch, setShowMatch] = useState(false);
  const [matchedUser, setMatchedUser] = useState(null);
  const [isWeddingDay, setIsWeddingDay] = useState(false);
  const [eventName, setEventName] = useState('');
  const [liveStats, setLiveStats] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [bouquetUsed, setBouquetUsed] = useState(false);
  const [bouquetLoading, setBouquetLoading] = useState(false);
  const [isBouquetMatch, setIsBouquetMatch] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [myEvents, setMyEvents] = useState([]);
  const [activeEventId, setActiveEventId] = useState(null);
  const [showEventSwitcher, setShowEventSwitcher] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  useEffect(() => {
    fetchProfiles();
    checkWeddingDay();
    fetchLiveStats();
    fetchNotifications();
    fetchMyEvents();
    checkBouquetUsed();
  }, []);
  useEffect(() => {
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, []);
  const checkBouquetUsed = async () => {
    try {
      const res = await axios.get(`${API}/bouquet-toss/status`);
      setBouquetUsed(res.data.used);
    } catch {}
  };
  const fetchMyEvents = async () => {
    try {
      const res = await axios.get(`${API}/events/mine`);
      setMyEvents(res.data.events || []);
      setActiveEventId(res.data.active_event_id);
    } catch {}
  };
  const handleSwitchEvent = async (eventId) => {
    if (eventId === activeEventId || isSwitching) return;
    setIsSwitching(true);
    try {
      const res = await axios.post(`${API}/events/switch`, { event_id: eventId });
      setActiveEventId(eventId);
      setMyEvents(prev => prev.map(e => ({ ...e, is_active: e.id === eventId })));
      updateUser({ event_id: eventId });
      setShowEventSwitcher(false);
      toast.success(`Switched to ${res.data.event.bride_name} & ${res.data.event.groom_name}`);
      setCurrentIndex(0);
      setProfiles([]);
      setIsLoading(true);
      await Promise.all([fetchProfiles(), checkWeddingDay(), fetchLiveStats()]);
    } catch {
      toast.error('Failed to switch event');
    } finally { setIsSwitching(false); }
  };
  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`);
      setUnreadCount(res.data.unread_count);
      setNotifications(res.data.notifications || []);
    } catch {}
  };
  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`);
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };
  const checkWeddingDay = async () => {
    try {
      const res = await axios.get(`${API}/events/wedding-day-mode`);
      setIsWeddingDay(res.data.is_wedding_day);
      setEventName(res.data.event_name || '');
    } catch {}
  };
  const fetchLiveStats = async () => {
    try {
      const res = await axios.get(`${API}/events/live-stats`);
      setLiveStats(res.data);
    } catch {}
  };
  const fetchProfiles = async () => {
    try {
      const res = await axios.get(`${API}/discover`);
      setProfiles(res.data.profiles);
    } catch {} finally { setIsLoading(false); }
  };
  const triggerConfetti = useCallback(() => {
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 3500);
  }, []);
  const handleSwipe = async (action) => {
    if (currentIndex >= profiles.length) return;
    const targetUser = profiles[currentIndex];
    setSwipeClass(action === 'like' ? 'dv-swipe-right' : 'dv-swipe-left');
    try {
      const res = await axios.post(`${API}/swipe`, { target_user_id: targetUser.id, action });
      if (res.data.is_match) {
        setMatchedUser(res.data.match.matched_user);
        setIsBouquetMatch(false);
        setTimeout(() => { setShowMatch(true); if (isWeddingDay) triggerConfetti(); }, 400);
        fetchLiveStats();
      }
    } catch {}
    setTimeout(() => { setSwipeClass(''); setCurrentIndex(prev => prev + 1); }, 400);
  };
  const handleBouquetToss = async () => {
    setBouquetLoading(true);
    try {
      const res = await axios.post(`${API}/bouquet-toss`);
      setBouquetUsed(true);
      setMatchedUser(res.data.matched_user);
      setIsBouquetMatch(true);
      setShowMatch(true);
      triggerConfetti();
      fetchLiveStats();
      toast.success('The bouquet found someone for you!');
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.detail?.includes('already used')) {
        setBouquetUsed(true);
        toast.error("You've already used your bouquet toss!");
      } else if (err.response?.status === 404) {
        toast.error('No available guests right now.');
      } else {
        toast.error('Bouquet toss failed. Try again!');
      }
    } finally { setBouquetLoading(false); }
  };
  const currentProfile = profiles[currentIndex];
  const activeEvent = myEvents.find(e => e.id === activeEventId);
  const formatTime = (dateStr) => {
    try { return formatDistanceToNow(new Date(dateStr), { addSuffix: true }); } catch { return ''; }
  };

  return (
    <div className="dv" data-testid="discover-page">
      <IOSInstallPrompt />
      {showConfetti && <ConfettiEffect />}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inclusive+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&display=swap');
        .dv { min-height: 100dvh; background: linear-gradient(180deg, #FFFDF8 60%, #FBF6EF 100%); max-width: 28rem; margin: 0 auto; position: relative; font-family: 'Inclusive Sans', sans-serif; }
        .dv::before {
          content: ''; position: fixed; inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.018'/%3E%3C/svg%3E");
          pointer-events: none; z-index: 0;
        }
        .dv-header {
          position: sticky; top: 0; z-index: 20;
          background: rgba(255,253,248,0.96); backdrop-filter: blur(16px);
          border-bottom: 1px solid rgba(20,19,19,0.06);
          padding: 1rem 1.5rem; display: flex; justify-content: center; align-items: center; position: relative;
        }
        .dv-logo { width: 2.75rem; height: 2.75rem; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: #141313; box-shadow: 0 2px 12px rgba(20,19,19,0.1); }
        .dv-actions { position: absolute; right: 1.5rem; display: flex; align-items: center; gap: 0.25rem; }
        .dv-icon-btn {
          background: none; border: none; cursor: pointer; color: #6E6B62; padding: 0.4rem;
          display: flex; align-items: center; justify-content: center; transition: color 0.2s ease; position: relative;
        }
        .dv-icon-btn:hover { color: #141313; }
        .dv-badge {
          position: absolute; top: 0; right: 0; width: 0.9rem; height: 0.9rem;
          background: #141313; color: #FFFDF8; border-radius: 50%;
          font-family: 'Inclusive Sans', sans-serif; font-size: 0.45rem;
          display: flex; align-items: center; justify-content: center;
        }
        .dv-switcher-wrap { position: absolute; left: 1.5rem; }
        .dv-switcher-btn {
          display: flex; align-items: center; gap: 0.3rem; background: none; border: none; cursor: pointer; padding: 0;
          font-family: 'Inclusive Sans', sans-serif; font-size: 0.52rem; font-weight: 600;
          letter-spacing: 0.12em; text-transform: uppercase; color: #6E6B62; transition: color 0.2s ease; max-width: 8rem;
        }
        .dv-switcher-btn:hover { color: #141313; }
        .dv-switcher-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 6rem; }
        .dv-switcher-panel {
          position: absolute; top: calc(100% + 0.75rem); left: 0; width: 16rem;
          background: #FFFDF8; border: 1px solid rgba(20,19,19,0.08);
          box-shadow: 0 16px 48px -8px rgba(20,19,19,0.18); z-index: 30; border-radius: 1rem;
          animation: dv-panel-in 0.2s cubic-bezier(0.16,1,0.3,1) both;
        }
        @keyframes dv-panel-in { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        .dv-switcher-head { padding: 1rem 1.25rem 0.75rem; border-bottom: 1px solid rgba(20,19,19,0.06); }
        .dv-switcher-title { font-family: 'Inclusive Sans', sans-serif; font-size: 1rem; font-weight: 700; color: #141313; letter-spacing: -0.02em; margin: 0; }
        .dv-switcher-list { padding: 0.5rem 0; }
        .dv-switcher-row {
          width: 100%; background: none; border: none; padding: 0.875rem 1.25rem;
          display: flex; align-items: center; gap: 0.75rem; text-align: left; cursor: pointer; transition: background 0.15s ease;
        }
        .dv-switcher-row:hover { background: rgba(20,19,19,0.03); }
        .dv-switcher-row.active { background: rgba(20,19,19,0.04); cursor: default; }
        .dv-switcher-dot { width: 0.45rem; height: 0.45rem; border-radius: 50%; flex-shrink: 0; background: rgba(20,19,19,0.12); }
        .dv-switcher-dot.active { background: #141313; }
        .dv-switcher-info { flex: 1; min-width: 0; }
        .dv-switcher-event-name { font-family: 'Inclusive Sans', sans-serif; font-size: 0.95rem; font-weight: 600; color: #141313; margin: 0 0 0.1rem; letter-spacing: -0.02em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .dv-switcher-event-date { font-family: 'Inclusive Sans', sans-serif; font-size: 0.55rem; font-weight: 500; color: #6E6B62; margin: 0; letter-spacing: 0.04em; }
        .dv-switcher-check { color: #141313; flex-shrink: 0; }
        .dv-switcher-footer { border-top: 1px solid rgba(20,19,19,0.06); padding: 0.875rem 1.25rem; }
        .dv-switcher-join { width: 100%; background: none; border: none; padding: 0; font-family: 'Inclusive Sans', sans-serif; font-size: 0.55rem; font-weight: 600; letter-spacing: 0.18em; text-transform: uppercase; color: #141313; cursor: pointer; text-align: left; opacity: 0.8; transition: opacity 0.2s ease; }
        .dv-switcher-join:hover { opacity: 1; }
        .dv-notif-wrap { position: relative; }
        .dv-notif-panel { position: absolute; top: calc(100% + 0.75rem); right: -1rem; width: 18rem; background: #FFFDF8; border: 1px solid rgba(20,19,19,0.08); box-shadow: 0 16px 48px -8px rgba(20,19,19,0.18); z-index: 30; border-radius: 1rem; animation: dv-notif-in 0.2s cubic-bezier(0.16,1,0.3,1) both; }
        @keyframes dv-notif-in { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        .dv-notif-head { display: flex; align-items: center; justify-content: space-between; padding: 1rem 1.25rem 0.75rem; border-bottom: 1px solid rgba(20,19,19,0.06); }
        .dv-notif-title { font-family: 'Inclusive Sans', sans-serif; font-size: 1rem; font-weight: 700; color: #141313; letter-spacing: -0.02em; }
        .dv-notif-close { background: none; border: none; cursor: pointer; color: #6E6B62; font-size: 0.65rem; padding: 0.2rem; transition: color 0.2s ease; line-height: 1; }
        .dv-notif-close:hover { color: #141313; }
        .dv-notif-list { max-height: 20rem; overflow-y: auto; }
        .dv-notif-row { width: 100%; background: none; border: none; border-bottom: 1px solid rgba(20,19,19,0.04); padding: 0.875rem 1.25rem; display: flex; align-items: flex-start; gap: 0.75rem; text-align: left; cursor: pointer; transition: background 0.15s ease; }
        .dv-notif-row:hover { background: rgba(20,19,19,0.03); }
        .dv-notif-row.unread { background: rgba(20,19,19,0.025); }
        .dv-notif-row:last-child { border-bottom: none; }
        .dv-notif-dot { width: 0.45rem; height: 0.45rem; border-radius: 50%; flex-shrink: 0; margin-top: 0.35rem; }
        .dv-notif-dot.match { background: #141313; }
        .dv-notif-dot.message { background: #F4A26B; }
        .dv-notif-dot.read { opacity: 0.2; }
        .dv-notif-info { flex: 1; min-width: 0; }
        .dv-notif-msg { font-family: 'Inclusive Sans', sans-serif; font-size: 0.7rem; font-weight: 500; color: #141313; margin: 0 0 0.2rem; line-height: 1.4; letter-spacing: 0.02em; }
        .dv-notif-row.unread .dv-notif-msg { font-weight: 600; }
        .dv-notif-time { font-family: 'Inclusive Sans', sans-serif; font-size: 0.52rem; font-weight: 500; color: #6E6B62; margin: 0; letter-spacing: 0.04em; }
        .dv-notif-empty { padding: 2.5rem 1.25rem; text-align: center; }
        .dv-notif-empty-p { font-family: 'Inclusive Sans', sans-serif; font-size: 1rem; font-style: italic; font-weight: 500; color: #6E6B62; margin: 0; }
        .dv-main { position: relative; z-index: 10; padding: 1rem 1rem 9rem; }
        .dv-wedding { background: #141313; padding: 1rem 1.25rem; display: flex; align-items: center; gap: 1rem; margin-bottom: 1rem; border-radius: 1rem; }
        .dv-wedding-icon { width: 2.25rem; height: 2.25rem; background: rgba(255,253,248,0.12); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #FFFDF8; flex-shrink: 0; }
        .dv-wedding-title { font-family: 'Inclusive Sans', sans-serif; font-size: 1.1rem; font-weight: 600; font-style: italic; color: #FFFDF8; margin: 0 0 0.1rem; }
        .dv-wedding-sub { font-family: 'Inclusive Sans', sans-serif; font-size: 0.58rem; font-weight: 500; color: rgba(255,253,248,0.55); margin: 0; letter-spacing: 0.05em; }
        .dv-stats { display: grid; grid-template-columns: 1fr 1px 1fr 1px 1fr; border: 1px solid rgba(20,19,19,0.06); margin-bottom: 1rem; border-radius: 1rem; overflow: hidden; }
        .dv-stat { padding: 0.75rem 0; text-align: center; }
        .dv-stat-num { font-family: 'Inclusive Sans', sans-serif; font-size: 1.35rem; font-weight: 700; color: #141313; line-height: 1; letter-spacing: -0.02em; }
        .dv-stat-lbl { font-family: 'Inclusive Sans', sans-serif; font-size: 0.45rem; font-weight: 600; letter-spacing: 0.15em; text-transform: uppercase; color: #6E6B62; margin-top: 0.15rem; }
        .dv-stat-sep { background: rgba(20,19,19,0.06); }
        .dv-card { background: #FFFDF8; border: 1px solid rgba(20,19,19,0.06); overflow: hidden; transition: transform 0.4s cubic-bezier(0.16,1,0.3,1), opacity 0.4s ease; margin-bottom: 1rem; border-radius: 1.25rem; }
        .dv-swipe-right { transform: translateX(150%) rotate(12deg); opacity: 0; }
        .dv-swipe-left  { transform: translateX(-150%) rotate(-12deg); opacity: 0; }
        .dv-card-img { aspect-ratio: 3/4; overflow: hidden; background: #f5f2ec; position: relative; }
        .dv-card-img img { width: 100%; height: 100%; object-fit: cover; }
        .dv-card-no-photo { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #b8b2a8; }
        .dv-card-overlay { position: absolute; bottom: 0; left: 0; right: 0; padding: 3rem 1.5rem 1.5rem; background: linear-gradient(to top, rgba(20,19,19,0.9) 0%, rgba(20,19,19,0.5) 50%, transparent 100%); }
        .dv-card-name { font-family: 'Inclusive Sans', sans-serif; font-size: 2.1rem; font-weight: 600; color: #FFFDF8; margin: 0 0 0.2rem; letter-spacing: -0.03em; line-height: 1.02; }
        .dv-card-relation { font-family: 'Inclusive Sans', sans-serif; font-size: 0.6rem; font-weight: 500; color: rgba(255,253,248,0.65); letter-spacing: 0.08em; margin: 0; }
        .dv-card-body { padding: 1.25rem 1.5rem; border-top: 1px solid rgba(20,19,19,0.04); display: flex; flex-direction: column; gap: 0.875rem; }
        .dv-card-bio { font-family: 'Inclusive Sans', sans-serif; font-size: 1.05rem; font-style: italic; font-weight: 500; color: #141313; margin: 0; line-height: 1.5; }
        .dv-card-fact { border-left: 1.5px solid rgba(20,19,19,0.2); padding: 0.5rem 0.875rem; }
        .dv-card-fact p { font-family: 'Inclusive Sans', sans-serif; font-size: 0.92rem; font-style: italic; font-weight: 500; color: #6E6B62; margin: 0; line-height: 1.5; }
        .dv-interests { display: flex; flex-wrap: wrap; gap: 0.35rem; }
        .dv-interest { font-family: 'Inclusive Sans', sans-serif; font-size: 0.55rem; font-weight: 500; letter-spacing: 0.08em; color: #6E6B62; border: 1px solid rgba(20,19,19,0.1); padding: 0.25rem 0.625rem; border-radius: 999px; }
        .dv-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; min-height: 22rem; padding: 2rem; }
        .dv-empty-diamond { font-size: 0.4rem; color: rgba(244,162,107,0.6); letter-spacing: 0.5em; font-family: 'Inclusive Sans', sans-serif; margin-bottom: 2rem; }
        .dv-empty-title { font-family: 'Inclusive Sans', sans-serif; font-size: 1.9rem; font-weight: 600; color: #141313; margin: 0 0 0.4rem; letter-spacing: -0.03em; }
        .dv-empty-sub { font-family: 'Inclusive Sans', sans-serif; font-size: 0.65rem; font-weight: 500; color: #6E6B62; margin: 0 0 2rem; letter-spacing: 0.04em; line-height: 1.6; }
        .dv-empty-btn { font-family: 'Inclusive Sans', sans-serif; font-size: 0.62rem; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; color: #FFFDF8; background: #141313; border: none; padding: 0.9rem 2rem; cursor: pointer; border-radius: 999px; box-shadow: 0 4px 20px rgba(20,19,19,0.18); }
        .dv-empty-btn:hover { background: #000000; }
        .dv-bouquet { border-top: 1px solid rgba(20,19,19,0.06); padding: 2rem 1rem; text-align: center; margin-top: 0.5rem; }
        .dv-bouquet-eyebrow { font-family: 'Inclusive Sans', sans-serif; font-size: 0.5rem; font-weight: 600; letter-spacing: 0.25em; text-transform: uppercase; color: #141313; opacity: 0.7; margin-bottom: 0.5rem; display: flex; align-items: center; gap: 0.5rem; justify-content: center; }
        .dv-bouquet-eyebrow-line { width: 1rem; height: 1px; background: #141313; opacity: 0.4; }
        .dv-bouquet-title { font-family: 'Inclusive Sans', sans-serif; font-size: 1.5rem; font-weight: 600; color: #141313; margin: 0 0 0.4rem; letter-spacing: -0.02em; }
        .dv-bouquet-sub { font-family: 'Inclusive Sans', sans-serif; font-size: 0.62rem; font-weight: 500; color: #6E6B62; margin: 0 0 1.5rem; line-height: 1.6; letter-spacing: 0.03em; }
        .dv-bouquet-btn { display: inline-flex; align-items: center; gap: 0.4rem; font-family: 'Inclusive Sans', sans-serif; font-size: 0.62rem; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; color: #FFFDF8; background: #141313; border: none; padding: 0.9rem 1.75rem; cursor: pointer; border-radius: 999px; box-shadow: 0 4px 20px rgba(20,19,19,0.18); }
        .dv-bouquet-btn:hover { background: #000000; }
        .dv-bouquet-btn:disabled { opacity: 0.4; cursor: not-allowed; box-shadow: none; }
        .dv-bouquet-used { border-top: 1px solid rgba(20,19,19,0.06); padding: 1.25rem; text-align: center; font-family: 'Inclusive Sans', sans-serif; font-size: 0.58rem; font-weight: 500; color: #6E6B62; letter-spacing: 0.08em; }
        .dv-swipe-bar { position: fixed; bottom: 4.5rem; left: 0; right: 0; padding: 1.25rem 2rem; background: linear-gradient(to top, #FFFDF8 55%, transparent 100%); z-index: 15; }
        .dv-swipe-btns { max-width: 28rem; margin: 0 auto; display: flex; justify-content: center; gap: 3rem; align-items: center; }
        .dv-pass-btn { width: 3.5rem; height: 3.5rem; border-radius: 50%; background: #FFFDF8; border: 1px solid rgba(20,19,19,0.1); color: #6E6B62; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.25s cubic-bezier(0.16,1,0.3,1); box-shadow: 0 2px 12px -4px rgba(20,19,19,0.1); }
        .dv-pass-btn:hover { border-color: #141313; color: #141313; transform: scale(1.06); }
        .dv-pass-btn:active { transform: scale(0.95); }
        .dv-like-btn { width: 4rem; height: 4rem; border-radius: 50%; background: #141313; border: none; color: #FFFDF8; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.25s cubic-bezier(0.16,1,0.3,1); box-shadow: 0 8px 24px -6px rgba(20,19,19,0.4); }
        .dv-like-btn:hover { background: #000000; transform: scale(1.06); }
        .dv-like-btn:active { transform: scale(0.95); }
        .dv-nav { position: fixed; bottom: 1.25rem; left: 50%; transform: translateX(-50%); background: rgba(255,253,248,0.97); backdrop-filter: blur(16px); border: 1px solid rgba(20,19,19,0.08); border-radius: 9999px; padding: 0.625rem 2rem; display: flex; align-items: center; gap: 2rem; z-index: 20; box-shadow: 0 4px 24px -8px rgba(20,19,19,0.12); }
        .dv-nav-btn { background: none; border: none; cursor: pointer; color: #6E6B62; padding: 0.35rem; display: flex; align-items: center; justify-content: center; transition: color 0.2s ease; }
        .dv-nav-btn.active { color: #141313; }
        .dv-nav-btn:hover { color: #141313; }
        .dv-match-body { text-align: center; padding: 2.5rem 2rem; display: flex; flex-direction: column; align-items: center; gap: 1.25rem; }
        .dv-match-icon { width: 4rem; height: 4rem; background: #141313; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #FFFDF8; box-shadow: 0 8px 24px rgba(20,19,19,0.3); }
        .dv-match-title { font-family: 'Inclusive Sans', sans-serif; font-size: 2.3rem; font-weight: 600; color: #141313; margin: 0 0 0.2rem; letter-spacing: -0.03em; line-height: 1.03; }
        .dv-match-sub { font-family: 'Inclusive Sans', sans-serif; font-size: 0.65rem; font-weight: 500; color: #6E6B62; margin: 0; letter-spacing: 0.05em; }
        .dv-match-avatar { width: 5rem; height: 5rem; border-radius: 50%; overflow: hidden; border: 1px solid rgba(20,19,19,0.08); }
        .dv-match-avatar img { width: 100%; height: 100%; object-fit: cover; }
        .dv-match-avatar-empty { width: 5rem; height: 5rem; border-radius: 50%; background: #f5f2ec; border: 1px solid rgba(20,19,19,0.08); display: flex; align-items: center; justify-content: center; color: #6E6B62; }
        .dv-match-actions { display: flex; gap: 0.75rem; width: 100%; padding-top: 0.5rem; }
        .dv-match-dismiss { flex: 1; height: 3rem; background: #FFFDF8; border: 1.5px solid rgba(20,19,19,0.15); border-radius: 999px; font-family: 'Inclusive Sans', sans-serif; font-size: 0.6rem; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: #6E6B62; cursor: pointer; transition: all 0.2s ease; }
        .dv-match-dismiss:hover { border-color: #6E6B62; color: #555; }
        .dv-match-msg { flex: 1; height: 3rem; background: #141313; border: none; border-radius: 999px; font-family: 'Inclusive Sans', sans-serif; font-size: 0.6rem; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: #FFFDF8; cursor: pointer; transition: opacity 0.2s ease; box-shadow: 0 4px 16px rgba(20,19,19,0.18); }
        .dv-match-msg:hover { background: #000000; }
        .dv-match-diamond { font-size: 0.4rem; color: rgba(244,162,107,0.6); letter-spacing: 0.5em; font-family: 'Inclusive Sans', sans-serif; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 0.8s linear infinite; }
      `}</style>

      <header className="dv-header">
        {myEvents.length > 0 && (
          <div className="dv-switcher-wrap">
            <button className="dv-switcher-btn" onClick={() => setShowEventSwitcher(v => !v)} data-testid="event-switcher-btn">
              <span className="dv-switcher-name">
                {activeEvent ? `${activeEvent.bride_name} & ${activeEvent.groom_name}` : 'Switch'}
              </span>
              <ChevronDown size={10} strokeWidth={1.5} style={{ transition: 'transform 0.2s ease', transform: showEventSwitcher ? 'rotate(180deg)' : 'none', flexShrink: 0 }} />
            </button>
            {showEventSwitcher && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 29 }} onClick={() => setShowEventSwitcher(false)} />
                <div className="dv-switcher-panel">
                  <div className="dv-switcher-head"><p className="dv-switcher-title">Your Weddings</p></div>
                  <div className="dv-switcher-list">
                    {myEvents.map(event => (
                      <button key={event.id} className={`dv-switcher-row ${event.is_active ? 'active' : ''}`} onClick={() => handleSwitchEvent(event.id)} disabled={isSwitching} data-testid={`switch-event-${event.id}`}>
                        <div className={`dv-switcher-dot ${event.is_active ? 'active' : ''}`} />
                        <div className="dv-switcher-info">
                          <p className="dv-switcher-event-name">{event.bride_name} & {event.groom_name}</p>
                          {event.wedding_date && (
                            <p className="dv-switcher-event-date">{new Date(event.wedding_date).toLocaleDateString('en-GB', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                          )}
                        </div>
                        {event.is_active && <Check size={12} strokeWidth={1.5} className="dv-switcher-check" />}
                      </button>
                    ))}
                  </div>
                  <div className="dv-switcher-footer">
                    <button className="dv-switcher-join" onClick={() => { setShowEventSwitcher(false); navigate('/'); }}>+ Join another wedding</button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        <div className="dv-logo"><TweddiLogo /></div>
        <div className="dv-actions">
          <div className="dv-notif-wrap">
            <button className="dv-icon-btn" onClick={() => { setShowNotifs(v => !v); if (!showNotifs) markAllRead(); }} data-testid="matches-btn">
              <Bell size={17} strokeWidth={1.5} />
              {unreadCount > 0 && <span className="dv-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>}
            </button>
            {showNotifs && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 29 }} onClick={() => setShowNotifs(false)} />
                <div className="dv-notif-panel">
                  <div className="dv-notif-head">
                    <span className="dv-notif-title">Notifications</span>
                    <button className="dv-notif-close" onClick={() => setShowNotifs(false)}>✕</button>
                  </div>
                  {notifications.length === 0 ? (
                    <div className="dv-notif-empty"><p className="dv-notif-empty-p">Nothing yet.</p></div>
                  ) : (
                    <div className="dv-notif-list">
                      {notifications.slice(0, 10).map((n) => (
                        <button key={n.id} className={`dv-notif-row ${!n.read ? 'unread' : ''}`} onClick={() => { setShowNotifs(false); if (n.match_id) navigate(`/chat/${n.match_id}`); else navigate('/matches'); }}>
                          <div className={`dv-notif-dot ${n.type} ${n.read ? 'read' : ''}`} />
                          <div className="dv-notif-info">
                            <p className="dv-notif-msg">{n.message}</p>
                            <p className="dv-notif-time">{formatTime(n.created_at)}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          <button className="dv-icon-btn" onClick={() => { logout(); navigate('/'); }} data-testid="logout-btn">
            <LogOut size={17} strokeWidth={1.5} />
          </button>
        </div>
      </header>

      <main className="dv-main">
        {isWeddingDay && (
          <div className="dv-wedding" data-testid="wedding-day-banner">
            <div className="dv-wedding-icon"><Sparkles size={16} strokeWidth={1.5} /></div>
            <div>
              <p className="dv-wedding-title">It's the big day.</p>
              <p className="dv-wedding-sub">{eventName}</p>
            </div>
          </div>
        )}
        {liveStats && (
          <div className="dv-stats" data-testid="live-stats-bar">
            <div className="dv-stat"><div className="dv-stat-num">{liveStats.total_guests}</div><div className="dv-stat-lbl">Guests</div></div>
            <div className="dv-stat-sep" />
            <div className="dv-stat"><div className="dv-stat-num">{liveStats.total_matches}</div><div className="dv-stat-lbl">Matches</div></div>
            <div className="dv-stat-sep" />
            <div className="dv-stat"><div className="dv-stat-num">{liveStats.today_matches}</div><div className="dv-stat-lbl">Today</div></div>
          </div>
        )}
        {isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '22rem' }}>
            <div className="spin" style={{ width: '1.25rem', height: '1.25rem', border: '1px solid #eee', borderTopColor: '#141313', borderRadius: '50%' }} />
          </div>
        ) : currentProfile ? (
          <div className={`dv-card ${swipeClass}`} data-testid="profile-card">
            <div className="dv-card-img">
              {currentProfile.photo_url
                ? <img src={getPhotoUrl(currentProfile.photo_url)} alt={currentProfile.name} />
                : <div className="dv-card-no-photo"><User size={56} strokeWidth={1} /></div>
              }
              <div className="dv-card-overlay">
                <p className="dv-card-name">{currentProfile.name}{currentProfile.age ? `, ${currentProfile.age}` : ''}</p>
                {currentProfile.relationship_to_couple && <p className="dv-card-relation">{currentProfile.relationship_to_couple}</p>}
              </div>
            </div>
            <div className="dv-card-body">
              {currentProfile.bio && <p className="dv-card-bio">"{currentProfile.bio}"</p>}
              {currentProfile.fun_fact && <div className="dv-card-fact"><p>{currentProfile.fun_fact}</p></div>}
              {currentProfile.interests?.length > 0 && (
                <div className="dv-interests">
                  {currentProfile.interests.map(i => <span key={i} className="dv-interest">{i}</span>)}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="dv-empty">
            <div className="dv-empty-diamond">◆ &nbsp; ◆ &nbsp; ◆</div>
            <h3 className="dv-empty-title">You've seen everyone.</h3>
            <p className="dv-empty-sub">You've seen all guests at this event.<br/>Check back later or view your matches.</p>
            <button className="dv-empty-btn" onClick={() => navigate('/matches')} data-testid="view-matches-btn">View Your Matches</button>
          </div>
        )}
        {!isLoading && !bouquetUsed && (
          <div className="dv-bouquet" data-testid="bouquet-toss-section">
            <div className="dv-bouquet-eyebrow">
              <div className="dv-bouquet-eyebrow-line" />Feeling Lucky?<div className="dv-bouquet-eyebrow-line" />
            </div>
            <h3 className="dv-bouquet-title">Bouquet Toss.</h3>
            <p className="dv-bouquet-sub">Get randomly matched with another guest.<br/>One toss per event.</p>
            <button className="dv-bouquet-btn" onClick={handleBouquetToss} disabled={bouquetLoading} data-testid="bouquet-toss-btn">
              {bouquetLoading
                ? <div className="spin" style={{ width: '0.875rem', height: '0.875rem', border: '1.5px solid rgba(255,253,248,0.3)', borderTopColor: '#FFFDF8', borderRadius: '50%' }} />
                : <><Sparkles size={12} strokeWidth={1.5} /> Toss the Bouquet</>
              }
            </button>
          </div>
        )}
        {bouquetUsed && (
          <div className="dv-bouquet-used" data-testid="bouquet-toss-used">◆ &nbsp; You've used your bouquet toss</div>
        )}
      </main>

      {currentProfile && !isLoading && (
        <div className="dv-swipe-bar">
          <div className="dv-swipe-btns">
            <button className="dv-pass-btn" onClick={() => handleSwipe('pass')} data-testid="pass-btn"><X size={20} strokeWidth={1.5} /></button>
            <button className="dv-like-btn" onClick={() => handleSwipe('like')} data-testid="like-btn">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
            </button>
          </div>
        </div>
      )}

      <nav className="dv-nav">
        <button className="dv-nav-btn active" onClick={() => navigate('/discover')} data-testid="nav-discover">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
        </button>
        <button className="dv-nav-btn" onClick={() => navigate('/matches')} data-testid="nav-matches"><MessageCircle size={18} strokeWidth={1.5} /></button>
        <button className="dv-nav-btn" onClick={() => navigate('/profile-setup')} data-testid="nav-profile"><User size={18} strokeWidth={1.5} /></button>
      </nav>

      <Dialog open={showMatch} onOpenChange={setShowMatch}>
        <DialogContent style={{ background: '#FFFDF8', border: '1px solid rgba(20,19,19,0.08)', borderRadius: '1.25rem', maxWidth: '22rem', padding: '0' }}>
          <div className="dv-match-body">
            <div className="dv-match-icon">
              {isBouquetMatch ? <Sparkles size={22} strokeWidth={1.5} /> : <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>}
            </div>
            <div>
              <p className="dv-match-title">{isBouquetMatch ? 'Bouquet Caught.' : "It's a Match."}</p>
              <p className="dv-match-sub">{isBouquetMatch ? `The bouquet landed on ${matchedUser?.name}` : `You and ${matchedUser?.name} liked each other`}</p>
            </div>
            {matchedUser?.photo_url
              ? <div className="dv-match-avatar"><img src={getPhotoUrl(matchedUser.photo_url)} alt={matchedUser.name} /></div>
              : <div className="dv-match-avatar-empty"><User size={24} strokeWidth={1} /></div>
            }
            <div className="dv-match-actions">
              <button className="dv-match-dismiss" onClick={() => setShowMatch(false)} data-testid="keep-swiping-btn">Keep Swiping</button>
              <button className="dv-match-msg" onClick={() => { setShowMatch(false); navigate('/matches'); }} data-testid="send-message-btn">Send Message</button>
            </div>
            <div className="dv-match-diamond">◆ &nbsp; ◆ &nbsp; ◆</div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
